import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';

import { Button, Flex, HelperText, Hr, Input, Modal, Typography } from 'components';

import { useProducts } from 'features/products';

import { brazilianRealFormat, clearCurrencyToNumber, moneyMask, request } from 'app/utils';
import { useSchool } from 'features/school';
import { useAlert } from 'features/alert';
import { ProductJourneyRelationalMetricFormattingEnum } from 'features/journeys/types/ProductJourneyRelationalMetricFormattingEnum';
import { AxiosError } from 'axios';

interface JourneyRelationalMetricsModalProps {
  refetch: (journeyMetricUuid: string, showLoading?: boolean) => void;
  onClose: () => void;
  relationalMetric: {
    relationalMetricUuid: string;
    period?: number;
  };
}

const schema = yup
  .object()
  .shape({
    value: yup.string().required('Você precisa informar o valor da custódia'),
  })
  .required();

const JourneyRelationalMetricsModal = ({ refetch, onClose, relationalMetric: receivedRelationalMetric }: JourneyRelationalMetricsModalProps) => {
  const { productSlug } = useParams();

  const { productDetails } = useProducts();
  const { apiGatewayUrl } = useSchool();
  const { setAlert, clearAlert, alert } = useAlert();

  const {
    handleSubmit,
    // register,
    setValue,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<{ value: number | string }>({
    resolver: yupResolver(schema),
    defaultValues: {
      value: '',
    },
  });

  const onSubmit = useCallback(
    async (formData: { period?: number; value: number | string }) => {
      const data = formData;

      data.value = clearCurrencyToNumber(data.value);
      data.period = receivedRelationalMetric.period || undefined;

      request<{ apiResultData: string }>({
        method: 'PATCH',
        url: `/api/v1/School/AssessJourneyRelationalMetricPeriodValue/${productSlug}/${receivedRelationalMetric.relationalMetricUuid}`,
        baseURL: apiGatewayUrl,
        params: data,
      })
        .then(() => {
          setAlert({ type: 'success', message: 'Sua custódia foi alterada com sucesso.' });
          refetch(receivedRelationalMetric.relationalMetricUuid, false);
        })
        .catch(
          (
            error: AxiosError<{
              apiResultMessage: string;
            }>
          ) => {
            let message = 'Ocorreu um erro ao salvar sua custódia.';

            if (error?.response?.data?.apiResultMessage) {
              message = error?.response?.data?.apiResultMessage;
            }

            setAlert({ type: 'error', message });
          }
        );
    },
    [productSlug, receivedRelationalMetric]
  );

  const currentRelationalMetric = useMemo(
    () =>
      productDetails?.journey?.relationalMetrics?.find(
        (relationalMetric) => relationalMetric?.uuid === receivedRelationalMetric.relationalMetricUuid
      ),
    [productDetails, receivedRelationalMetric.relationalMetricUuid]
  );

  useEffect(() => {
    return () => {
      clearAlert();
    };
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal onClose={onClose}>
        <Flex flexGrow={0} width={{ d: 540 }} flexDirection="column">
          <Flex flexGrow={0}>
            <Typography type="type4" px={3} py="12px" upper fontWeight="bold">
              ATUALIZAR {currentRelationalMetric?.assessmentValueLegendText}
            </Typography>
          </Flex>

          <Hr m={0} />

          <Flex p={3} flexDirection="column" bg="gray_100">
            <Typography type="type1" upper color="gray_500" mb={1}>
              {currentRelationalMetric?.assessmentValueLegendText} atual
            </Typography>

            {!!receivedRelationalMetric.period && (
              <Typography type="type2" color="dark_350" fontWeight="bold" mb={2}>
                {currentRelationalMetric?.periodAxisLegends[receivedRelationalMetric.period - 1]}
              </Typography>
            )}

            <Typography type="type4" color="dark_350" fontWeight="bold" mb={3}>
              <>
                {receivedRelationalMetric.period ? (
                  <>
                    {currentRelationalMetric?.formatting === ProductJourneyRelationalMetricFormattingEnum.Money
                      ? brazilianRealFormat(currentRelationalMetric?.values[receivedRelationalMetric.period - 1] || 0)
                      : currentRelationalMetric?.values[receivedRelationalMetric.period]}
                  </>
                ) : (
                  <>
                    {currentRelationalMetric?.formatting === ProductJourneyRelationalMetricFormattingEnum.Money
                      ? brazilianRealFormat(currentRelationalMetric?.periodValue || 0)
                      : currentRelationalMetric?.periodValue}
                  </>
                )}
              </>
            </Typography>

            <Input
              onChange={(e) => {
                setValue(
                  'value',
                  currentRelationalMetric?.formatting === ProductJourneyRelationalMetricFormattingEnum.Money
                    ? moneyMask(e.target.value)
                    : e.target.value
                );
              }}
              value={
                watch('value')
                // currentRelationalMetric?.formatting === ProductJourneyRelationalMetricFormattingEnum.Numeric
                //   ? clearCurrencyToNumber(watch('value')) || 0
                //   : watch('value') || 0
              }
              type={currentRelationalMetric?.formatting === ProductJourneyRelationalMetricFormattingEnum.Numeric ? 'number' : 'text'}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder={`Informar ${currentRelationalMetric?.assessmentValueLegendText.toLowerCase()}`}
            />

            {!!errors?.value && <HelperText type="error">{errors?.value?.message}</HelperText>}

            {!!alert?.message && (
              <Typography color={alert?.type === 'success' ? 'success_400' : 'alert_400'} fontWeight="bold" mt={3}>
                {alert?.message}
              </Typography>
            )}
          </Flex>

          <Hr m={0} />

          <Button disabled={isSubmitting} type="submit" borderTopLeftRadius={0} borderTopRightRadius={0}>
            Enviar
          </Button>
        </Flex>
      </Modal>
    </form>
  );
};

export default JourneyRelationalMetricsModal;
